import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from "react-toastify";

import priceListApi from "../api/priceListApi";
import supplierApi from "../api/supplierApi";

const productTypes = [
  { value: 0, label: "Gia hạn phần mềm" },
  { value: 1, label: "Hóa đơn" },
];
const pageNo = "PriceListPage";
function PriceListPage() {
  // --- Trạng thái tìm kiếm và lọc ---
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchParams, setSearchParams] = useState({
    searchKeyword: "",
    productType: 0,
    supplierId: -1,
    supplier: "",
  });
  // --- Trạng thái DataGrid và phân trang ---
  const [priceList, setPriceList] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // --- Trạng thái Modal thêm, sửa mặt hàng ---
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [currentPriceListToEdit, setCurrentPriceListToEdit] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersArray, setSuppliersArray] = useState([]);

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await supplierApi.getSupplier();

        if (response.success) {
          setSuppliers([
            {
              EsId: -1,
              EsName: "Tất cả",
            },
            {
              EsId: 0,
              EsName: "Relisoft",
            },
            ...response.data.map((supplier) => ({
              EsId: supplier.EsId,
              EsName: supplier.EsName,
            })),
          ]);
          setSuppliersArray([
            {
              EsId: 0,
              EsName: "Relisoft",
            },
            ...response.data.map((supplier) => ({
              EsId: supplier.EsId,
              EsName: supplier.EsName,
            })),
          ]);
        }
      } catch (error) {
        toast.error(
          error.message || "Đã xảy ra lỗi khi tìm kiếm nhà cung cấp.",
        );
      } finally {
      }
    };
    fetchSuppliers();
  }, []);

  // --- Hàm tìm kiếm mặt hàng ---
  const fetchPriceList = useCallback(
    async (searchKeyword, page, pageSize, productType, supplierId) => {
      setLoading(true);
      try {
        const response = await priceListApi.search(
          searchKeyword,
          page,
          pageSize,
          productType,
          supplierId,
          null,
        );

        if (response.success) {
          setPriceList(response.data);
          setTotalRowCount(response.pagination?.totalRecords || 0);
        } else {
          setPriceList([]);
          setTotalRowCount(0);
        }
      } catch (error) {
        toast.error(error.toString() || "Đã xảy ra lỗi khi tìm kiếm mặt hàng.");
        setPriceList([]);
        setTotalRowCount(0);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // --- Phân trang DataGrid ---
  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
    fetchPriceList(
      searchParams.searchKeyword,
      newModel.page,
      newModel.pageSize,
      searchParams.productType,
      searchParams.supplierId,
    );
  };

  const handleSearch = () => {
    fetchPriceList(
      searchParams.searchKeyword,
      paginationModel.page,
      paginationModel.pageSize,
      searchParams.productType,
      searchParams.supplierId,
    );
  };

  const handleAddNewProduct = () => {
    setCurrentPriceListToEdit({
      priceListId: null,
      productGroupNo: "",
      productGroupName: "",
      productNo: "",
      productName: "",
      unitName: "",
      unitPrice: 0,
      quantity: 1,
      vatPercent: 0,
      extraPrice: 0,
      originalPrice: 0,
      productType: 0,
      supplierId: 0,
      supplier: "",
      isActive: true,
    });
    setFormErrors({});
    setOpenAddEditModal(true);
  };

  // --- Sửa mặt hàng ---
  const handleEditProduct = async (priceListId) => {
    try {
      const response = await priceListApi.get(priceListId);
      setCurrentPriceListToEdit(response.data);
      setFormErrors({});
      setOpenAddEditModal(true);
    } catch (error) {
      toast.error(error);
    }
  };

  // --- Đóng Modal thêm/sửa ---
  const handleCloseAddEditModal = () => {
    setCurrentPriceListToEdit(null);
    setFormErrors({});
    setOpenAddEditModal(false);
  };

  // --- Lưu mặt hàng (thêm mới hoặc cập nhật) ---
  const handleSaveProduct = async () => {
    setLoading(true);
    const errors = {};
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      const response = await priceListApi.upsert(currentPriceListToEdit);
      if (response.success) {
        toast.success(response.message || "Lưu mặt hàng thành công!");
        handleCloseAddEditModal();
        handleSearch();
      } else {
        toast.error(response.message || "Lưu mặt hàng thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi lưu mặt hàng.");
    } finally {
      setLoading(false);
    }
  };

  // --- Mở Modal xác nhận xóa ---
  const handleDeleteProduct = async (priceListId) => {
    setLoading(true);
    try {
      const response = await priceListApi.delete(priceListId);
      if (response.success) {
        toast.success("Xóa mặt hàng thành công!");
        handleSearch();
      } else {
        toast.error(response.message || "Xóa mặt hàng thất bại.");
      }
    } catch (error) {
      toast.error(error.toString() || "Đã xảy ra lỗi khi xóa mặt hàng.");
    } finally {
      setLoading(false);
    }
  };

  // --- Định nghĩa các cột cho DataGrid ---
  const columns = [
    {
      field: "productGroupNo",
      headerName: "Mã nhóm",
      headerAlign: "center",
      width: 150,
    },
    {
      field: "productGroupName",
      headerName: "Tên nhóm",
      headerAlign: "center",
      width: 250,
    },
    {
      field: "productNo",
      headerName: "Mã mặt hàng",
      headerAlign: "center",
      width: 150,
    },
    {
      field: "productName",
      headerName: "Tên mặt hàng",
      headerAlign: "center",
      width: 250,
    },
    {
      field: "unitPrice",
      headerName: "Đơn giá",
      headerAlign: "center",
      align: "right",
      width: 110,
    },
    {
      field: "quantity",
      headerName: "SL",
      headerAlign: "center",
      align: "right",
      width: 80,
    },
    {
      field: "unitName",
      headerName: "Đơn vị",
      headerAlign: "center",
      align: "center",
      width: 100,
    },
    {
      field: "vatPercent",
      headerName: "% thuế",
      headerAlign: "center",
      align: "right",
      width: 90,
    },
    {
      field: "extraPrice",
      headerName: "Phí khởi tạo",
      headerAlign: "center",
      align: "right",
      width: 110,
    },
    {
      field: "originalPrice",
      headerName: "Giá gốc",
      headerAlign: "center",
      align: "right",
      width: 110,
    },
    {
      field: "supplier",
      headerName: "Nhà cung cấp",
      headerAlign: "center",
      width: 150,
    },
    {
      field: "isActive",
      headerName: "Kích hoạt",
      headerAlign: "center",
      type: "boolean",
    },
    {
      field: "actions",
      headerName: "Chức năng",
      headerAlign: "center",
      type: "actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditProduct(params.row.priceListId)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={() => handleDeleteProduct(params.row.priceListId)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchPriceList(
      searchParams.searchKeyword,
      paginationModel.page,
      paginationModel.pageSize,
      searchParams.productType,
      searchParams.supplierId,
    );
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Khung tìm kiếm */}
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={1} alignItems="flex-end">
            <Grid sx={{ minWidth: "250px" }}>
              <TextField
                select
                label="Loại mặt hàng"
                fullWidth
                value={searchParams?.productType || 0}
                onChange={(e) => {
                  setSearchParams((prev) => ({
                    ...prev,
                    productType: e.target.value,
                  }));
                  if (e.target.value !== 1)
                    setSearchParams((prev) => ({
                      ...prev,
                      supplierId: null,
                      supplier: "",
                    }));
                }}
                size="small"
              >
                {productTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {searchParams?.productType === 1 && (
              <Grid sx={{ minWidth: "250px" }}>
                <TextField
                  select
                  label="Nhà cung cấp"
                  fullWidth
                  value={
                    searchParams?.supplierId === null
                      ? -1
                      : searchParams?.supplierId
                  }
                  onChange={(e) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      supplierId: e.target.value,
                    }))
                  }
                  size="small"
                >
                  {suppliers.map((option) => (
                    <MenuItem key={option.EsId} value={option.EsId}>
                      {option.EsName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid sx={{ minWidth: "250px" }}>
              <TextField
                label="Từ khóa"
                fullWidth
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid
              sx={{
                flexGrow: 1,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Grid>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => {
                    setPaginationModel({
                      page: 0,
                      pageSize: paginationModel.pageSize,
                    });
                    fetchPriceList(
                      searchParams.searchKeyword,
                      0,
                      paginationModel.pageSize,
                      searchParams.productType,
                      searchParams.supplierId,
                    );
                  }}
                  sx={{ height: "40px" }}
                >
                  Tìm kiếm
                </Button>
              </Grid>
              <Grid>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddNewProduct}
                  sx={{ height: "40px" }}
                >
                  Thêm mặt hàng mới
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* DataGrid danh sách mặt hàng */}
        <Box sx={{ flexGrow: 1, width: "100%" }}>
          <DataGrid
            rows={priceList}
            columns={columns}
            paginationMode="server"
            rowCount={totalRowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 20, 50, 100]}
            disableRowSelectionOnClick
            loading={loading}
            getRowId={(row) => row.priceListId}
            showCellVerticalBorder
            showColumnVerticalBorder
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  Không có mặt hàng nào được tìm thấy.
                </Box>
              ),
            }}
          />
        </Box>

        {/* Modal Thêm/Sửa mặt hàng */}
        <Dialog open={openAddEditModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {currentPriceListToEdit
              ? "Sửa thông tin mặt hàng"
              : "Thêm mặt hàng mới"}
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              select
              label="Loại mặt hàng"
              fullWidth
              value={currentPriceListToEdit?.productType || 0}
              onChange={(e) =>
                setCurrentPriceListToEdit((prev) => ({
                  ...prev,
                  productType: e.target.value,
                }))
              }
              size="small"
              sx={{ mb: 1 }}
            >
              {productTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {currentPriceListToEdit?.productType === 1 && (
              // <Autocomplete
              //   freeSolo
              //   options={suppliersArray}
              //   renderInput={(params) => (
              //     <TextField
              //       {...params}
              //       label="Nhà cung cấp"
              //       variant="outlined"
              //     />
              //   )}
              //   onChange={(event, newValue) => {
              //     setCurrentPriceListToEdit((prev) => ({
              //       ...prev,
              //       supplier: newValue,
              //     }));
              //   }}
              //   onInputChange={(event, newInputValue) => {
              //     setCurrentPriceListToEdit((prev) => ({
              //       ...prev,
              //       supplier: newInputValue,
              //     }));
              //   }}
              //   size="small"
              //   sx={{ mb: 1 }}
              //   value={currentPriceListToEdit?.supplier || ""}
              // />
              <TextField
                select
                label="Nhà cung cấp"
                fullWidth
                value={
                  currentPriceListToEdit?.supplierId === null
                    ? 0
                    : currentPriceListToEdit?.supplierId
                }
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    supplierId: e.target.value === -1 ? null : e.target.value,
                  }))
                }
                size="small"
                sx={{ mb: 1 }}
              >
                {suppliersArray.map((option) => (
                  <MenuItem key={option.EsId} value={option.EsId}>
                    {option.EsName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Grid sx={{ display: "flex" }}>
              <TextField
                label="Mã nhóm"
                type="text"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.productGroupNo || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    productGroupNo: e.target.value,
                  }))
                }
                error={!!formErrors.productGroupNo}
                helperText={formErrors.productGroupNo}
                size="small"
                sx={{ mb: 1, flex: 1 }}
              />
              <TextField
                label="Tên nhóm"
                type="text"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.productGroupName || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    productGroupName: e.target.value,
                  }))
                }
                error={!!formErrors.productGroupName}
                helperText={formErrors.productGroupName}
                size="small"
                sx={{ mb: 1, flex: 2 }}
              />
            </Grid>
            <Grid sx={{ display: "flex" }}>
              <TextField
                label="Mã mặt hàng"
                type="text"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.productNo || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    productNo: e.target.value,
                  }))
                }
                error={!!formErrors.productNo}
                helperText={formErrors.productNo}
                size="small"
                sx={{ mb: 1, flex: 1 }}
              />
              <TextField
                label="Tên mặt hàng"
                type="text"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.productName || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    productName: e.target.value,
                  }))
                }
                error={!!formErrors.productName}
                helperText={formErrors.productName}
                size="small"
                sx={{ mb: 1, flex: 2 }}
              />
            </Grid>
            <Grid sx={{ display: "flex" }}>
              <TextField
                label="Đơn vị tính"
                type="text"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.unitName || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    unitName: e.target.value,
                  }))
                }
                error={!!formErrors.unitName}
                helperText={formErrors.unitName}
                size="small"
                sx={{ mb: 1, flex: 1 }}
              />
              <TextField
                label="Đơn giá"
                type="number"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.unitPrice || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    unitPrice: e.target.value,
                  }))
                }
                error={!!formErrors.unitPrice}
                helperText={formErrors.unitPrice}
                size="small"
                sx={{ mb: 1, flex: 1 }}
              />
            </Grid>
            <Grid sx={{ display: "flex" }}>
              <TextField
                label="Số lượng"
                type="number"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.quantity || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    quantity: e.target.value,
                  }))
                }
                error={!!formErrors.quantity}
                helperText={formErrors.quantity}
                size="small"
                sx={{ mb: 1, flex: 1 }}
              />
              <TextField
                label="% thuế"
                type="number"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.vatPercent || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    vatPercent: e.target.value,
                  }))
                }
                error={!!formErrors.vatPercent}
                helperText={formErrors.vatPercent}
                size="small"
                sx={{ mb: 1, flex: 1 }}
              />
            </Grid>

            {currentPriceListToEdit?.productType === 1 && (
              <TextField
                label="Phí khởi tạo"
                type="number"
                fullWidth
                variant="outlined"
                value={currentPriceListToEdit?.extraPrice || ""}
                onChange={(e) =>
                  setCurrentPriceListToEdit((prev) => ({
                    ...prev,
                    extraPrice: e.target.value,
                  }))
                }
                error={!!formErrors.extraPrice}
                helperText={formErrors.extraPrice}
                size="small"
                sx={{ mb: 1 }}
              />
            )}
            <TextField
              label="Giá gốc"
              type="number"
              fullWidth
              variant="outlined"
              value={currentPriceListToEdit?.originalPrice || ""}
              onChange={(e) =>
                setCurrentPriceListToEdit((prev) => ({
                  ...prev,
                  originalPrice: e.target.value,
                }))
              }
              error={!!formErrors.originalPrice}
              helperText={formErrors.originalPrice}
              size="small"
              sx={{ mb: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentPriceListToEdit?.isActive || false}
                  onChange={(event) =>
                    setCurrentPriceListToEdit((prev) => ({
                      ...prev,
                      isActive: event.target.checked,
                    }))
                  }
                  name="isActive"
                  color="primary"
                />
              }
              label="Kích hoạt"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleCloseAddEditModal()} color="primary">
              Hủy
            </Button>
            <Button
              onClick={handleSaveProduct}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default PriceListPage;
